'use client';

import { listByProductIds } from '@/api/public';
import type { ListProduct } from '@/api/types/public';
import YouMightLikeWaterFall, {
  type YouMightLikeTrackingHandlers,
} from '@/app/cart/components/YouMightLikeWaterFall';
import InfiniteScroll from '@/components/InfiniteScroll';
import ProductCard from '@/components/Product/ProductCard';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { StyleProductExposureItem } from '../hooks/styleProductExposureDedup';
import type { StyleHotProductProductRecommendConfig } from '../types';
import { useStyleProductRecommend } from '../hooks/useStyleProductRecommend';

const DEFAULT_RECOMMEND_TITLE = 'You may also like';
const STYLE_PRODUCT_RECOMMEND_MODULE_NAME = '商品推荐版位';
const STYLE_PRODUCT_RECOMMEND_TAB_NAME = 'You May Also Like';

type ProductTrackingHandler = (
  item: StyleProductExposureItem,
  index: number,
  moduleName: string,
  tabName: string,
) => void;

/** 与 campaigns/[slug] 的商品推荐区块保持同一套基础视觉 */
const RECOMMEND_SECTION_CLASS = {
  root: (isWap: boolean) =>
    `bg-white font-regular w-full ${isWap ? 'mx-auto pt-[24px] px-[8px]' : 'mx-auto pt-[40px] px-[48px]'} pb-[60px]`,
  title: (isWap: boolean) =>
    `font-bb text-center ${isWap ? 'text-[20px] leading-[24px] mb-[16px]' : 'text-[32px] leading-[40px] mb-[32px]'}`,
  grid: (isWap: boolean) =>
    isWap
      ? 'grid grid-cols-2 gap-x-[8px] gap-y-[40px] relative'
      : 'grid gap-x-[16px] gap-y-[24px] 2xl:grid-cols-4 xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 pb-[64px]',
  infiniteScroll: (isWap: boolean) =>
    isWap ? 'pt-[40px]' : 'h-[64px] flex items-center justify-center',
} as const;

interface ProductRecommendProps {
  config?: StyleHotProductProductRecommendConfig | null;
  deviceType: EPlatform;
  marketingPageId?: number;
  onProductClick?: ProductTrackingHandler;
  onProductExposure?: ProductTrackingHandler;
  onLoadComplete?: () => void;
}

/** 是否配置了可展示的推荐商品版位 */
export function isStyleHotProductProductRecommendEnabled(
  config?: StyleHotProductProductRecommendConfig | null,
): boolean {
  if (!config?.source_type) return false;
  if (config.source_type === 'algorithm') return true;
  if (config.source_type === 'group') return Number(config.group_id) > 0;
  return false;
}

/**
 * 商品推荐版位组件
 * 支持 source_type='group'（指定分组）和 source_type='algorithm'（算法推荐）两种模式
 */
export default function ProductRecommend({
  config,
  deviceType,
  marketingPageId,
  onProductClick,
  onProductExposure,
  onLoadComplete,
}: ProductRecommendProps) {
  if (!isStyleHotProductProductRecommendEnabled(config) || !config) {
    return null;
  }

  if (config.source_type === 'algorithm') {
    return (
      <AlgorithmRecommend
        deviceType={deviceType}
        title={config.section_title}
        cacheKey={`style-hot-product:${marketingPageId || 0}:${deviceType}:${config.algorithm_value || ''}`}
        onProductClick={onProductClick}
        onProductExposure={onProductExposure}
        onLoadComplete={onLoadComplete}
      />
    );
  }

  if (config.source_type === 'group' && config.group_id) {
    return (
      <GroupRecommend
        groupId={config.group_id}
        deviceType={deviceType}
        title={config.section_title}
        onProductClick={onProductClick}
        onProductExposure={onProductExposure}
        onLoadComplete={onLoadComplete}
      />
    );
  }

  return null;
}

interface GroupRecommendProps {
  groupId: number;
  deviceType: EPlatform;
  title?: string;
  onProductClick?: ProductTrackingHandler;
  onProductExposure?: ProductTrackingHandler;
  onLoadComplete?: () => void;
}

function GroupRecommend({
  groupId,
  deviceType,
  title,
  onProductClick,
  onProductExposure,
  onLoadComplete,
}: GroupRecommendProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const sectionTitle = title || DEFAULT_RECOMMEND_TITLE;
  const prevTokenRef = useRef<string | undefined>(undefined);
  const token = useStore((state) => state.token);
  const currency = useStore((state) => state.currency);
  const {
    data: list,
    originalData,
    loading,
    isReachingEnd,
    size,
    mutate,
    loadMore,
    pageSize,
    total,
    hasAttemptedFetch,
    hasFetchError,
  } = useStyleProductRecommend(groupId, currency?.name);
  const loadCompleteNotifiedRef = useRef(false);

  const notifyLoadComplete = useCallback(() => {
    if (loadCompleteNotifiedRef.current) return;
    loadCompleteNotifiedRef.current = true;
    onLoadComplete?.();
  }, [onLoadComplete]);

  useEffect(() => {
    if (!hasAttemptedFetch || loading) return;
    if (isReachingEnd || hasFetchError) {
      notifyLoadComplete();
    }
  }, [hasAttemptedFetch, hasFetchError, isReachingEnd, loading, notifyLoadComplete]);

  const updateProductsWishStatus = useCallback(async () => {
    try {
      if (list.length === 0) return;

      const productIds = list.map((item) => item.id || 0).filter((id) => id > 0);
      if (productIds.length === 0) return;

      const response = await listByProductIds({ product_ids: productIds.join(',') });
      if (response?.code !== 200 || !Array.isArray(response.data?.list)) return;

      const wishMap = response.data.list.reduce<Record<number, number>>((acc, item) => {
        if (item.id && typeof item.is_wish === 'number') {
          acc[item.id] = item.is_wish;
        }
        return acc;
      }, {});

      void mutate(
        originalData?.map((pageData) => ({
          ...pageData,
          data: {
            ...pageData.data,
            list: (pageData.data?.list || []).map((item) => ({
              ...item,
              is_wish: item.id && wishMap[item.id] !== undefined ? wishMap[item.id] : item.is_wish,
            })),
          },
        })),
        { revalidate: false },
      );
    } catch (error) {
      logger.error(error, 'Failed to update style hot product recommend wish status:');
    }
  }, [list, mutate, originalData]);

  useWhenTokenReady(() => {
    if (prevTokenRef.current === undefined) {
      prevTokenRef.current = token;
      return;
    }

    const prevToken = prevTokenRef.current;

    if (prevToken && !token) {
      void mutate(
        originalData?.map((pageData) => ({
          ...pageData,
          data: {
            ...pageData.data,
            list: (pageData.data?.list || []).map((item) => ({ ...item, is_wish: 2 })),
          },
        })),
        { revalidate: false },
      );
    }

    if (token && prevToken !== token) {
      void updateProductsWishStatus();
    }

    prevTokenRef.current = token;
  }, [token, updateProductsWishStatus]);

  const renderItem = useCallback(
    (item: ListProduct, index: number) => (
      <div key={`${item.shop_sku || item.id}-${index}`} className="bg-none">
        <ProductCard
          product={{ ...item, name: item.name || item.short_name || item.shop_sku || '' }}
          index={index}
          showTryOn={false}
          collectScene="周年庆风格爆品商品推荐"
          className={isWap ? 'bg-none' : 'h-full bg-none'}
          imageClassName={isWap ? 'h-[176px] rounded-[12px]' : undefined}
          showCorrelation
          handelClick={(productIndex, product) =>
            onProductClick?.(
              product,
              productIndex,
              STYLE_PRODUCT_RECOMMEND_MODULE_NAME,
              STYLE_PRODUCT_RECOMMEND_TAB_NAME,
            )
          }
          onInView={() =>
            onProductExposure?.(
              item,
              index,
              STYLE_PRODUCT_RECOMMEND_MODULE_NAME,
              STYLE_PRODUCT_RECOMMEND_TAB_NAME,
            )
          }
        />
      </div>
    ),
    [isWap, onProductClick, onProductExposure],
  );

  if (list.length === 0 && !loading) {
    return null;
  }

  return (
    <div className={RECOMMEND_SECTION_CLASS.root(isWap)}>
      <div className={RECOMMEND_SECTION_CLASS.title(isWap)}>{sectionTitle}</div>

      <div className={RECOMMEND_SECTION_CLASS.grid(isWap)}>
        {list.map((item, index) => renderItem(item, index))}
      </div>

      {!isReachingEnd && (
        <InfiniteScroll
          loading={loading}
          onReachBottom={loadMore}
          page={size}
          total={total}
          limit={pageSize}
          className={RECOMMEND_SECTION_CLASS.infiniteScroll(isWap)}
        />
      )}
    </div>
  );
}

interface AlgorithmRecommendProps {
  deviceType: EPlatform;
  title?: string;
  cacheKey: string;
  onProductClick?: ProductTrackingHandler;
  onProductExposure?: ProductTrackingHandler;
  onLoadComplete?: () => void;
}

function AlgorithmRecommend({
  deviceType,
  title,
  cacheKey,
  onProductClick,
  onProductExposure,
  onLoadComplete,
}: AlgorithmRecommendProps) {
  const token = useStore((state) => state.token);
  const tracking = useMemo<YouMightLikeTrackingHandlers>(
    () => ({
      onItemClick: (item, index) =>
        onProductClick?.(
          item,
          index,
          STYLE_PRODUCT_RECOMMEND_MODULE_NAME,
          STYLE_PRODUCT_RECOMMEND_TAB_NAME,
        ),
      onProductInView: (item, index) =>
        onProductExposure?.(
          item,
          index,
          STYLE_PRODUCT_RECOMMEND_MODULE_NAME,
          STYLE_PRODUCT_RECOMMEND_TAB_NAME,
        ),
      // 风格爆品推荐区目前只定义商品点击和曝光事件，空实现用于阻止回退到购物车埋点。
      onProductValidExposure: () => undefined,
      onModelExp: () => undefined,
      collectScene: '周年庆风格爆品商品推荐',
    }),
    [onProductClick, onProductExposure],
  );

  return (
    <YouMightLikeWaterFall
      key={`style-hot-product-recommend-${deviceType}-${token}`}
      recommendList={[]}
      deviceType={deviceType}
      sectionTitle={title || DEFAULT_RECOMMEND_TITLE}
      pcGridClassName={RECOMMEND_SECTION_CLASS.grid(false)}
      syncWishOnTokenChange
      cacheKey={cacheKey}
      tracking={tracking}
      onAllLoaded={onLoadComplete}
    />
  );
}
